// Shared formatting helpers.
// TAK8 operates in Perth, Australia - all amounts are AUD and dates use en-AU.

const currencyFormatter = new Intl.NumberFormat('en-AU', {
  style: 'currency',
  currency: 'AUD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatCurrency = (amount?: number | null): string =>
  currencyFormatter.format(Number.isFinite(amount as number) ? (amount as number) : 0);

export const formatDate = (date?: Date | string | null): string => {
  if (!date) return 'N/A';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (date?: Date | string | null): string => {
  if (!date) return 'N/A';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Whole days between two dates, ignoring the time of day. */
const daysFromToday = (date: Date): number => {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const target = new Date(date);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - startOfToday.getTime()) / 86_400_000);
};

export type RelativeDay = {
  label: string;
  /** True for today/tomorrow/overdue - the cases worth drawing the eye to. */
  isNear: boolean;
  isToday: boolean;
  isPast: boolean;
};

/** "Today", "Tomorrow", "in 3 days", "2 days ago" - context the raw date can't give. */
export const describeRelativeDay = (date?: Date | string | null): RelativeDay | null => {
  if (!date) return null;
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return null;

  const diff = daysFromToday(parsed);

  if (diff === 0) return { label: 'Today', isNear: true, isToday: true, isPast: false };
  if (diff === 1) return { label: 'Tomorrow', isNear: true, isToday: false, isPast: false };
  if (diff === -1) return { label: 'Yesterday', isNear: true, isToday: false, isPast: true };
  if (diff > 1 && diff <= 7) return { label: `in ${diff} days`, isNear: false, isToday: false, isPast: false };
  if (diff < -1 && diff >= -7) return { label: `${Math.abs(diff)} days ago`, isNear: false, isToday: false, isPast: true };

  return null;
};

/** "12s ago" / "3m ago" - used for the auto-refresh indicator. */
export const formatTimeAgo = (date: Date | null): string => {
  if (!date) return 'never';
  const seconds = Math.max(0, Math.round((Date.now() - date.getTime()) / 1000));
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  return `${hours}h ago`;
};
